import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { errorReporter } from '../services/error-reporter.service';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // Check if it's a chunk loading error
    const isChunkError = error.message?.includes('Loading chunk') ||
                        error.message?.includes('Failed to fetch dynamically imported module') ||
                        error.name === 'ChunkLoadError';

    if (isChunkError) {
      console.warn('Chunk loading error caught by ErrorBoundary:', error);
      // Clear caches and prepare for reload
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            if (name.includes('chunk') || name.includes('assets')) {
              caches.delete(name);
            }
          });
        });
      }
    }

    // Report error to backend (automatic email notification)
    errorReporter.reportError(error, {
      errorType: 'React_Error_Boundary',
      errorCode: error.name,
      additionalData: {
        componentStack: errorInfo.componentStack,
        isChunkError,
        pathname: window.location.pathname,
        search: window.location.search,
      },
    }).catch(reportingError => {
      // Don't let error reporting crash the app
      console.error('Failed to report error to backend:', reportingError);
    });
  }

  private handleRetry = () => {
    const isChunkError = this.state.error?.message?.includes('Loading chunk') ||
                        this.state.error?.message?.includes('Failed to fetch dynamically imported module') ||
                        this.state.error?.name === 'ChunkLoadError';

    if (isChunkError) {
      // For chunk errors, force page reload
      window.location.reload();
    } else {
      this.setState({ hasError: false, error: null });
    }
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isNotFound = this.state.error?.message?.includes('404') ||
                        this.state.error?.message?.includes('Not Found') ||
                        window.location.pathname.includes('/undefined') ||
                        window.location.pathname.includes('/null');

      const isChunkError = this.state.error?.message?.includes('Loading chunk') ||
                          this.state.error?.message?.includes('Failed to fetch dynamically imported module') ||
                          this.state.error?.name === 'ChunkLoadError';

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            bgcolor: 'background.default',
            p: 3,
          }}
        >
          <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center' }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />

            <Typography variant="h4" color={isChunkError ? 'warning.main' : 'error'} gutterBottom>
              {isNotFound ? 'Page Not Found' : isChunkError ? 'Update Available' : 'Something went wrong'}
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {isNotFound
                ? 'The page you are looking for could not be found.'
                : isChunkError
                ? 'A new version of the app is available. Please refresh the page to load the latest updates.'
                : 'We encountered an unexpected error. Please try again or return to the home page.'
              }
            </Typography>

            {this.props.showDetails && this.state.error && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 3,
                  fontFamily: 'monospace',
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  maxHeight: 200,
                  overflow: 'auto'
                }}
              >
                {this.state.error.message}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
              <Button
                variant={isChunkError ? "contained" : "outlined"}
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                color={isChunkError ? "warning" : "primary"}
              >
                {isChunkError ? 'Refresh Page' : 'Try Again'}
              </Button>
              {!isChunkError && (
                <Button
                  variant="contained"
                  startIcon={<HomeIcon />}
                  onClick={this.handleGoHome}
                >
                  Go Home
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}